import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  addDoc, 
  updateDoc,
  increment,
  doc,
  serverTimestamp,
  where,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import * as filter from 'leo-profanity';
import './NoticeboardPage.css';

const NoticeboardPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userLocation, setUserLocation] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const { currentUser } = useAuth();
  
  const MAX_POST_LENGTH = 500;
  const MAX_REPLY_LENGTH = 300;
  
  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'recommendations', name: 'Recommendations' }, // OT, specialists, services
    { id: 'support', name: 'Support & Chat' },
    { id: 'resources', name: 'Resources & Info' }
  ];

  const australianLocations = [
    'Sydney, NSW', 'Melbourne, VIC', 'Brisbane, QLD', 'Perth, WA',
    'Adelaide, SA', 'Gold Coast, QLD', 'Newcastle, NSW', 'Canberra, ACT',
    'Sunshine Coast, QLD', 'Wollongong, NSW', 'Hobart, TAS', 'Geelong, VIC',
    'Townsville, QLD', 'Cairns, QLD', 'Darwin, NT', 'Other'
  ];

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  useEffect(() => {
    // Set default display name and location from user profile
    if (currentUser) {
      const firstName = currentUser.displayName?.split(' ')[0] || currentUser.email.split('@')[0];
      setDisplayName(firstName);
    }
  }, [currentUser]);

  const fetchPosts = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      let postsQuery;
      
      if (selectedCategory === 'all') {
        postsQuery = query(
          collection(db, 'Noticeboard', 'Posts', 'AllPosts'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
      } else {
        postsQuery = query(
          collection(db, 'Noticeboard', 'Posts', 'AllPosts'),
          where('category', '==', selectedCategory),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
      }
      
      const querySnapshot = await getDocs(postsQuery);
      const postsList = [];
      
      querySnapshot.forEach(doc => {
        postsList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setPosts(postsList);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const formatPostContent = (content) => {
    // Simple link detection and formatting
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return content.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPostContent.trim() || !currentUser || !userLocation) {
      setError('Please fill in all fields including your location.');
      return;
    }

    if (newPostContent.length > MAX_POST_LENGTH) {
      setError(`Post must be ${MAX_POST_LENGTH} characters or less.`);
      return;
    }

   // Check for profanity
if (filter.check(newPostContent)) {
  setError('Please keep posts respectful and appropriate for our community.');
  return;
}

try {
  setError(null);
  
  const cleanContent = filter.clean(newPostContent);
  
  await addDoc(collection(db, 'Noticeboard', 'Posts', 'AllPosts'), {
        content: cleanContent,
        category: selectedCategory === 'all' ? 'support' : selectedCategory,
        createdBy: currentUser.email,
        displayName: displayName,
        location: userLocation,
        displayLocation: userLocation !== 'Other' ? userLocation : 'Australia',
        createdAt: serverTimestamp(),
        likes: 0,
        replies: [],
        reported: false,
        contentType: isValidUrl(cleanContent.trim()) ? 'link' : 'text'
      });
      
      setNewPostContent('');
      fetchPosts();
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post. Please try again.');
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim() || !currentUser) return;

    if (replyContent.length > MAX_REPLY_LENGTH) {
      setError(`Reply must be ${MAX_REPLY_LENGTH} characters or less.`);
      return;
    }

    if (filter.check(replyContent)) {
      setError('Please keep replies respectful and appropriate.');
      return;
    }
    
    try {
      setError(null);
      
      const cleanReply = filter.clean(replyContent);
      const postRef = doc(db, 'Noticeboard', 'Posts', 'AllPosts', postId);
      
      await updateDoc(postRef, {
        replies: arrayUnion({
          id: Date.now().toString(),
          content: cleanReply,
          createdBy: currentUser.email,
          displayName: displayName,
          location: userLocation,
          createdAt: new Date()
        })
      });
      
      setReplyContent('');
      setReplyingTo(null);
      fetchPosts();
    } catch (err) {
      console.error('Error replying:', err);
      setError('Failed to post reply. Please try again.');
    }
  };

  const handleLike = async (postId) => {
    if (!currentUser) return;
    
    try {
      const postRef = doc(db, 'Noticeboard', 'Posts', 'AllPosts', postId);
      
      await updateDoc(postRef, {
        likes: increment(1)
      });
      
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, likes: (post.likes || 0) + 1 } 
            : post
        )
      );
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const handleReport = async (postId) => {
    if (!currentUser) return;
    
    try {
      const postRef = doc(db, 'Noticeboard', 'Posts', 'AllPosts', postId);
      
      await updateDoc(postRef, {
        reported: true,
        reportedBy: arrayUnion(currentUser.email),
        reportedAt: serverTimestamp()
      });
      
      alert('Post has been reported to moderators. Thank you for helping keep our community safe.');
    } catch (err) {
      console.error('Error reporting post:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffSecs < 60) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (err) {
      return '';
    }
  };

  return (
    <div className="noticeboard-page">
      <header className="page-header">
        <h1>Community Noticeboard</h1>
        <div className="medical-disclaimer">
          <strong>Important:</strong> This is a community support space. 
          Content shared here is not medical advice. Always consult healthcare professionals for medical decisions.
        </div>
      </header>
      
      <div className="categories-tabs">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      
      <div className="new-post-container">
        <form onSubmit={handlePostSubmit}>
          <div className="post-form-header">
            <h3>Share with the Community</h3>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Display Name:</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                maxLength={50}
                required
              />
            </div>
            <div className="form-group">
              <label>Location:</label>
              <select
                value={userLocation}
                onChange={(e) => setUserLocation(e.target.value)}
                required
              >
                <option value="">Select location</option>
                {australianLocations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="new-post-content">
            <textarea
              placeholder="Share a question, recommendation, or support message... You can include links to helpful resources."
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              rows={4}
              maxLength={MAX_POST_LENGTH}
            />
            <div className="character-count">
              {newPostContent.length}/{MAX_POST_LENGTH}
            </div>
          </div>
          
          <div className="new-post-actions">
            <button 
              type="submit" 
              className="post-button"
              disabled={!newPostContent.trim() || !userLocation || !displayName}
            >
              Post to Community
            </button>
          </div>
        </form>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="empty-state">
          <span className="material-icons empty-state-icon">forum</span>
          <h2>No posts yet in this category</h2>
          <p>Be the first to share something with the community!</p>
        </div>
      ) : (
        <div className="posts-container">
          {posts.map(post => (
            <div key={post.id} className="post-card">
              <div className="post-header">
                <div className="post-author">
                  <div className="author-avatar">
                    {post.displayName?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="author-details">
                    <div className="author-name">
                      {post.displayName || 'Anonymous'}
                    </div>
                    <div className="author-location">
                      {post.displayLocation || post.location}
                    </div>
                    <div className="post-date">
                      {formatDate(post.createdAt)}
                    </div>
                  </div>
                </div>
                
                <div className="post-menu">
                  <button 
                    className="report-button"
                    onClick={() => handleReport(post.id)}
                    title="Report inappropriate content"
                  >
                    <span className="material-icons">flag</span>
                  </button>
                </div>
              </div>
              
              <div className="post-content">
                {post.contentType === 'link' ? (
                  <div dangerouslySetInnerHTML={{ __html: formatPostContent(post.content) }} />
                ) : (
                  post.content
                )}
              </div>
              
              <div className="post-actions">
                <button 
                  className="post-action-button like-button"
                  onClick={() => handleLike(post.id)}
                >
                  <span className="material-icons">thumb_up</span>
                  <span className="action-count">{post.likes || 0}</span>
                </button>
                
                <button 
                  className="post-action-button reply-button"
                  onClick={() => setReplyingTo(replyingTo === post.id ? null : post.id)}
                >
                  <span className="material-icons">reply</span>
                  <span className="action-count">{post.replies?.length || 0}</span>
                </button>
              </div>
              
              {/* Replies section */}
              {post.replies && post.replies.length > 0 && (
                <div className="replies-section">
                  {post.replies.map(reply => (
                    <div key={reply.id} className="reply-item">
                      <div className="reply-author">
                        <strong>{reply.displayName}</strong>
                        <span className="reply-location">{reply.location}</span>
                        <span className="reply-date">{formatDate(reply.createdAt)}</span>
                      </div>
                      <div className="reply-content">{reply.content}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply form */}
              {replyingTo === post.id && (
                <div className="reply-form">
                  <textarea
                    placeholder="Write a helpful reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={2}
                    maxLength={MAX_REPLY_LENGTH}
                  />
                  <div className="reply-actions">
                    <div className="character-count-small">
                      {replyContent.length}/{MAX_REPLY_LENGTH}
                    </div>
                    <button 
                      onClick={() => handleReply(post.id)}
                      disabled={!replyContent.trim()}
                      className="reply-submit-button"
                    >
                      Reply
                    </button>
                    <button 
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent('');
                      }}
                      className="reply-cancel-button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoticeboardPage;